/** News feed, filtered to the selected symbol. */

import type { NewsItem } from '@/api/types'
import { useNews } from '@/hooks/useQueries'

const SENTIMENT_TONE = {
  pos: 'text-up',
  neg: 'text-down',
  neu: 'text-ink-dim',
} as const

const CATEGORY_LABELS: Record<string, string> = {
  hack: 'tấn công',
  regulation: 'pháp lý',
  listing: 'niêm yết',
  protocol: 'giao thức',
  macro: 'vĩ mô',
  earnings: 'KQKD',
  dividend: 'cổ tức',
  issuance: 'phát hành',
  insider: 'nội bộ',
  flow: 'dòng tiền',
  general: 'chung',
}

export function NewsFeed({ ticker }: { ticker: string | null }) {
  const { data, isLoading } = useNews(ticker)

  if (isLoading) return <p className="p-3 text-2xs text-ink-dim">Đang tải tin…</p>
  if (!data?.length) {
    return (
      <p className="p-3 text-2xs text-ink-dim">
        {ticker ? `Không có tin nào gắn với ${ticker} trong 72h` : 'Không có tin mới'}
      </p>
    )
  }

  return (
    <ul className="divide-y divide-border">
      {data.map((item) => (
        <NewsRow key={item.url + item.published_at} item={item} />
      ))}
    </ul>
  )
}

function NewsRow({ item }: { item: NewsItem }) {
  return (
    <li className="px-3 py-1.5 hover:bg-panel-hover">
      <a href={item.url} target="_blank" rel="noreferrer" className="block">
        <div className="flex items-start gap-2">
          {/* Only importance 3 is allowed to raise a Telegram alert, so it is
              worth calling out here too. */}
          {item.importance === 3 && <span className="mt-0.5 text-warn" title="Tin quan trọng">●</span>}
          <span className="flex-1 text-xs leading-snug text-ink/90">{item.title}</span>
          <span className={`shrink-0 text-2xs ${SENTIMENT_TONE[item.sentiment_label]}`}>
            {item.sentiment_label === 'pos' ? '▲' : item.sentiment_label === 'neg' ? '▼' : '–'}
          </span>
        </div>
        <div className="mt-0.5 flex gap-2 text-2xs text-ink-dim">
          <span>{item.source}</span>
          <span>{CATEGORY_LABELS[item.category] ?? item.category}</span>
          {item.tickers.length > 0 && <span className="text-accent">{item.tickers.join(' ')}</span>}
          <span className="ml-auto">{relativeTime(item.published_at)}</span>
        </div>
      </a>
    </li>
  )
}

export function relativeTime(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minutes < 1) return 'vừa xong'
  if (minutes < 60) return `${minutes} phút trước`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} giờ trước`
  return `${Math.round(hours / 24)} ngày trước`
}
